import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { io, ManagerOptions, Socket, SocketOptions } from "socket.io-client";
import { useFlags } from "flagsmith/react";
import {
  ClientToServerEvents,
  FlagsmithTraits,
  ServerToClientEvents,
} from "@aichat/types/index";

type SocketContextType = {
  socket: Socket<ServerToClientEvents, ClientToServerEvents> | null;
  isConnected: boolean;
};

type SocketProviderProps = PropsWithChildren<{
  url: string;
  organizationId: string;
  apiKey: string;
  options: Partial<ManagerOptions & SocketOptions>;
  onConnect: (
    // eslint-disable-next-line no-unused-vars
    socket: Socket<ServerToClientEvents, ClientToServerEvents>,
  ) => void;
  onDisconnect?: () => void;
  conversationMedium?: "audio";
  projectId?: string;
}>;

const SocketContext = createContext<SocketContextType>({} as SocketContextType);

export function SocketProvider(props: SocketProviderProps) {
  const {
    url,
    organizationId,
    apiKey,
    options,
    onConnect,
    onDisconnect,
    conversationMedium,
    projectId,
  } = props;

  const [socket, setSocket] = useState<Socket<
    ServerToClientEvents,
    ClientToServerEvents
  > | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { digital_human } = useFlags([], [FlagsmithTraits.DIGITAL_HUMAN]);
  const channel = digital_human
    ? "digital-human"
    : conversationMedium === "audio"
      ? "webchat-audio"
      : "webchat-text";

  const memoizedOptions = useMemo(() => {
    return {
      ...options,
      query: {
        conversationMedium,
        organizationId,
        projectId,
        apiKey,
        channel,
      },
    };
  }, [
    channel,
    conversationMedium,
    organizationId,
    apiKey,
    options.auth,
    options.transports?.length,
    options.reconnectionDelay,
    options.forceNew,
  ]);

  useEffect(() => {
    if (!memoizedOptions.query.organizationId || !memoizedOptions.query.apiKey)
      return;

    const socketInstance: Socket = io(url, {
      ...memoizedOptions,
      withCredentials: true,
      path: "/socket.io",
    });

    setSocket(socketInstance);

    socketInstance.on("connect", () => {
      console.log("Socket connected");
    });

    socketInstance.on("connection:ready", () => {
      setIsConnected(true);
      onConnect(socketInstance);
    });

    socketInstance.on("disconnect", () => {
      console.log("Socket disconnected");
      if (!socketInstance.active) {
        setIsConnected(false);
        onDisconnect?.();
      }
    });

    return () => {
      socketInstance.disconnect();
    };
  }, [memoizedOptions, url]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {props.children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }

  return context;
}
